import { Component, OnInit, Input, ViewChild, ElementRef, HostListener, DOCUMENT } from '@angular/core';
import { SearchService } from 'src/app/services/search/search.service';
import { debounceTime } from 'rxjs/operators';
import { Question, Questionnaire, QuestionnaireService } from 'src/app/services/questionnaire/questionnaire.service';
import { faSearch, faTimes, faBars, faCaretDown, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { BuilderService } from 'src/app/services/builder/builder.service';
import { Subject } from 'rxjs';
import { SearchMenuService } from 'src/app/services/search-menu/search-menu.service';
import { BackdropService } from 'src/app/services/backdrop/backdrop.service';
import { WindowScrollService } from 'src/app/services/window-scroll/window-scroll.service';
import { ScreenSizeService } from 'src/app/services/screen-size/screen-size.service';
import { EditModeService } from 'src/app/services/edit-mode/edit-mode.service';
import { I18nHelperService } from '@xref/ngx';
import { PageScrollService } from 'ngx-page-scroll-core';
import { Inject } from '@angular/core';


@Component({
  selector: 'xqnr-search',
  templateUrl: './search.component.pug',
  styleUrls: ['./search.component.scss'],
  standalone: false
})
export class SearchComponent implements OnInit {

  @Input() questionnaireBuild: Questionnaire = { id: '', title: '', questions: [] };

  @ViewChild('searchInputRef') searchInputRef: ElementRef;
  @ViewChild('searchResultsRef') searchResultsRef: ElementRef;
  @ViewChild('searchResultsInnerRef') searchResultsInnerRef: ElementRef;

  searchInput = '';
  searchInput$ = new Subject();

  searchResults: any[] = [];
  showSearchResults = false;

  searching = false;
  addingQuestion = false;

  searchResultsMaxHeight = 'auto';
  searchResultsScroll = false;

  fa: any = {
    search: faSearch,
    times: faTimes,
    bars: faBars,
    caretDown: faCaretDown,
    circleNotch: faCircleNotch
  };

  constructor(
    private search: SearchService,
    private builder: BuilderService,
    private searchMenu: SearchMenuService,
    private backdrop: BackdropService,
    private windowScroll: WindowScrollService,
    private screenSize: ScreenSizeService,
    public editMode: EditModeService,
    public i18nHelper: I18nHelperService,
    private qs: QuestionnaireService,
    private animateScrollService: PageScrollService,
    @Inject(DOCUMENT) private document: any
  ) { }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): any {
    if (this.searchResults.length > 0)
      setTimeout(() => this.setResultsScroll(), 0);
  }

  ngOnInit(): void {
    // Listen for search submits
    this.searchInput$
      .pipe(debounceTime(300))
      .subscribe((keyword: string) => {
        if (keyword.length >= 0) {
          this.getSearchResults(keyword);
        } else {
          this.clearSearchResults();
        }
      });

    // Listen for search requests coming from service
    this.search.searchFor$.subscribe((keyword: string) => {
      this.searchInput = keyword;
      this.handleSearchChanged(keyword);
    });

    // When the language changes the search results will be wrong so clear them
    this.i18nHelper.localeChanged$.subscribe(() => {
      this.clearSearchResults();
    });
  }

  getSearchResultsSuccess = (data: any) => {
    const results = data.slice(0, 10);

    // Mark each search result that is already in the built questionnaire
    results.forEach((item: Question) => {
      item['alreadyInQuestionnaire'] = this.builder.searchResultAlreadyInQuestionnaire(item);
    });

    this.searchResults = results;
    this.showSearchResults = true;
    this.searching = false;

    // Small desktop screens need a scrollable results list
    setTimeout(() => this.setResultsScroll(), 0);

    // Mobile devices with full-width layout
    if (this.screenSize.lte(this.screenSize.md))
      this.blockWindowScrollOnMobile();
  };

  getSearchResultsError = (err: any) => {
    console.error(err);
    this.searching = false;
  };

  getSearchResults = (searchStr: string) => {
    this.searching = true;

    if (searchStr.length >= 2) {
      this.search.getSearchResults(searchStr, this.i18nHelper.locale)
        .subscribe(this.getSearchResultsSuccess, this.getSearchResultsError);
    }
  };

  clearSearchResults() {
    this.searchResults = [];
    this.searchInput = '';

    this.showSearchResults = false;

    this.unblockWindowScrollOnMobile();
  }

  handleSearchChanged(keyword: string) {
    if (keyword.length <= 0)
      this.clearSearchResults();
    else
      this.searchInput$.next(keyword);
  }

  addQuestionSuccess = (data: Question) => {
    this.clearSearchResults();

    this.addingQuestion = false;
    this.builder.addQuestion(data);

    // Scroll to botom of page
    this.animateScrollService.scroll({
      document: this.document,
      scrollTarget: '#bottom',
      duration: 350,
    });
  };

  addQuestionError = (err: any) => {
    console.error(err);
    this.addingQuestion = false;
  };

  addQuestion(result: Question) {
    if (!this.addingQuestion) {
      this.addingQuestion = true;

      this.qs.addQuestion(result.id, this.builder.questionnaire.id).subscribe(
        this.addQuestionSuccess,
        this.addQuestionError
      );
    }
  }

  // 'X' button clicks
  handleClearIconClicked() {
    this.clearSearchResults();
    this.focusSearchField();
  }

  handleSearchIconClicked() {
    this.focusSearchField();
  }

  handleCompetencyClick(competency: string) {
    this.search.searchFor(competency);
  }

  showMenu() {
    this.searchMenu.show();
    this.backdrop.show();
  }

  scrollResultsToTop() {
    this.searchResultsRef.nativeElement.scrollTop = 0;
  }

  focusSearchField() {
    this.searchInputRef.nativeElement.focus();
  }

  setResultsScroll() {
    const headerHeightOffset = 127; // 60+60 with 7 extra to avoid some mobile browser issues
    const winHeight = window.innerHeight - headerHeightOffset;
    const searchResultsShouldScroll = this.searchResultsInnerRef.nativeElement.offsetHeight >= winHeight;

    this.searchResultsScroll = searchResultsShouldScroll;
    this.searchResultsMaxHeight = `${winHeight}px`;
  }

  blockWindowScrollOnMobile() {
    this.windowScroll.preventWindowScroll();
    this.scrollResultsToTop();
  }

  unblockWindowScrollOnMobile() {
    this.windowScroll.allowWindowScroll();
    this.scrollResultsToTop();
  }
}
